import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Card, CardBody, CardHeader } from 'reactstrap';
import CompanyContext from '../../../auth/CompanyContext';
import Loading from '../../Loading/Loading';

const PortalTransactionSummary = (props) => {
    const companyCtx = useContext(CompanyContext);
    const hasCompanyPortalDatabase = String(props.companySettings?.CompanyPortalDatabase || '').trim() !== '';
    const { callback, onWidgetLoad, onAddCallback } = props;

    const getValues = useCallback((width) => {
        let col1 = 0, col2 = 0, graphHeight = 0;
        switch (width) {
            case 4:
                col1 = 12; col2 = 12; graphHeight = 350;
                break;
            case 6:
                col1 = 12; col2 = 12; graphHeight = 350;
                break;
            case 12:
                col1 = 12; col2 = 12; graphHeight = 350;
                break;
            default:
        }
        return { col1, col2, graphHeight };
    }, []);

    const initialValues = getValues(props.width);
    const [groupedTransactions, setGroupedTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [col1, setCol1] = useState(initialValues.col1);
    const [col2, setCol2] = useState(initialValues.col2);
    const [graphHeight, setGraphHeight] = useState(initialValues.graphHeight);
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);

    const refreshRef = useRef(() => { });
    const styleSettingsRef = useRef({});

    const toNumber = useCallback((val) => {
        const parsed = Number(val);
        return Number.isFinite(parsed) ? parsed : 0;
    }, []);

    const parseSqlDate = useCallback((value, toUTCDateOnly = false) => {
        if (!value) return null;
        const parsed = new Date(value);
        if (Number.isNaN(parsed.getTime())) return null;
        if (!toUTCDateOnly) return parsed;
        return new Date(parsed.getUTCFullYear(), parsed.getUTCMonth(), parsed.getUTCDate());
    }, []);

    const getPeriodStart = useCallback(() => {
        const period = (styleSettingsRef.current || {}).tranSummaryPeriod;
        const date = new Date();

        if (period === 'Month to Date') return new Date(date.getFullYear(), date.getMonth(), 1);
        if (period === 'Quarter to Date') {
            const quarter = Math.floor(date.getMonth() / 3);
            return new Date(date.getFullYear(), quarter * 3, 1);
        }
        if (period === 'Year to Date') return new Date(date.getFullYear(), 0, 1);
        if (period === '6 Month') return new Date(date.getFullYear(), date.getMonth() - 6, date.getDate());
        if (period === '12 Month') return new Date(date.getFullYear() - 1, date.getMonth(), date.getDate());
        return null;
    }, []);

    const groupTransactions = useCallback((transactions = []) => {
        const sorted = [...transactions].sort((a, b) => {
            const aDate = a && a.tradeDate ? a.tradeDate.getTime() : 0;
            const bDate = b && b.tradeDate ? b.tradeDate.getTime() : 0;
            return bDate - aDate;
        });

        const groupedMap = new Map();
        const grouped = [];

        for (let i = 0; i < sorted.length; i++) {
            const row = sorted[i] || {};
            const key = row.tranCode || row.tranName || 'Unknown';
            if (!groupedMap.has(key)) {
                const entry = {
                    key,
                    tranName: row.tranName || row.tranCode || 'Unknown',
                    count: 0,
                    totalTradeAmount: 0
                };
                groupedMap.set(key, entry);
                grouped.push(entry);
            }

            const entry = groupedMap.get(key);
            entry.count += 1;
            entry.totalTradeAmount += toNumber(row.tradeAmount);
        }

        return grouped;
    }, [toNumber]);

    const getStyleSettings = useCallback(async () => {
        if (!hasCompanyPortalDatabase) return;

        const styleNames = ['tranSummaryPeriod'];
        const params = new URLSearchParams();
        styleNames.forEach(name => params.append('styleNames[]', name));

        try {
            const results = await fetch(`${process.env.REACT_APP_PORTAL_API_URL}/api/crm/getStyleSettings?${params.toString()}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + props.idToken || "",
                    'X-Database': companyCtx.company
                }
            });
            const response = await results.json();
            styleSettingsRef.current = response.styleSettings || {};
        } catch (err) {
            console.error(err);
            styleSettingsRef.current = {};
        }
    }, [props.idToken, companyCtx.company, hasCompanyPortalDatabase]);

    const getTransactions = useCallback(async () => {
        if (!hasCompanyPortalDatabase) {
            setGroupedTransactions([]);
            setLoading(false);
            return;
        }

        setLoading(true);

        const from = getPeriodStart();
        const to = new Date();
        const household = typeof props.selectedHousehold === 'object'
            ? props.selectedHousehold && props.selectedHousehold.household
            : (props.selectedHousehold || '');

        const params = new URLSearchParams({
            household,
            port: household,
            consolidated: 'true',
            from: from ? from.toISOString() : '',
            to: to.toISOString()
        });

        try {
            const results = await fetch(`${process.env.REACT_APP_PORTAL_API_URL}/api/crm/transactions?${params.toString()}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + props.idToken || "",
                    'X-Database': companyCtx.company
                }
            });
            const response = await results.json();

            if (response.code !== 200) {
                setGroupedTransactions([]);
                setLoading(false);
                setFromDate(from);
                setToDate(to);
                return;
            }

            const rawTransactions = Array.isArray(response.data) ? response.data : [];
            const transactions = rawTransactions.map(row => ({
                ...row,
                tradeDate: parseSqlDate(row.tradeDate, true),
                settleDate: parseSqlDate(row.settleDate, true)
            }));

            let effectiveFromDate = from;
            if (!effectiveFromDate) {
                for (let i = 0; i < transactions.length; i++) {
                    const tradeDate = transactions[i] && transactions[i].tradeDate;
                    if (!tradeDate) continue;
                    if (!effectiveFromDate || effectiveFromDate > tradeDate) {
                        effectiveFromDate = tradeDate;
                    }
                }
            }

            setGroupedTransactions(groupTransactions(transactions));
            setLoading(false);
            setFromDate(effectiveFromDate);
            setToDate(to);
        } catch (err) {
            console.log(err);
            setGroupedTransactions([]);
            setLoading(false);
            setFromDate(from);
            setToDate(to);
        }
    }, [getPeriodStart, props.selectedHousehold, props.idToken, companyCtx.company, parseSqlDate, groupTransactions, hasCompanyPortalDatabase]);

    const onSubmit = useCallback(() => {
        getTransactions();
    }, [getTransactions]);

    const runRefresh = useCallback(() => {
        setLoading(true);
        onSubmit();
    }, [onSubmit]);

    useEffect(() => {
        refreshRef.current = runRefresh;
    }, [runRefresh]);

    useEffect(() => {
        let active = true;

        const init = async () => {
            await getStyleSettings();
            if (!active) return;

            callback((width) => {
                if (!active) return;
                const values = getValues(width);
                setGraphHeight(values.graphHeight);
                setCol1(values.col1);
                setCol2(values.col2);
            });

            if (onWidgetLoad) {
                onWidgetLoad();
                onAddCallback(() => {
                    refreshRef.current();
                });
            }
        };

        init();

        return () => {
            active = false;
        };
    }, [hasCompanyPortalDatabase]);

    const formatCurrency = (value) => {
        const num = toNumber(value);
        return '$' + num.toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    };

    const getDateHeader = () => {
        return (
            (fromDate ? fromDate.toLocaleDateString('en-US') : 'N/A')
            + ' to ' +
            (toDate ? toDate.toLocaleDateString('en-US') : 'N/A')
        );
    };

    void col1;
    void col2;

    return (
        <Card className='w-100 m-0'>
            <CardHeader className="d-flex justify-content-between align-items-center">
                <h4>Transaction Summary</h4>
                {props.icon}
            </CardHeader>
            <CardBody className='p-3'>
                {loading && <Loading />}
                {!loading && (
                    <>
                        <div className='tranSummSnapshotHeader'>
                            <label>{getDateHeader()}</label>
                        </div>
                        {groupedTransactions.length > 0 ? (
                            <div style={{ minHeight: graphHeight + 'px' }}>
                                <table className='tranSummSnapshot'>
                                    <tbody>
                                        {groupedTransactions.slice(0, 7).map((row, idx) => (
                                            <tr key={`${row.key}-${idx}`}>
                                                <td className='tranSummLabel'>
                                                    <label style={{ fontWeight: 'bold' }}>{row.tranName}</label> <label>({row.count})</label>
                                                </td>
                                                <td className='tranSummValue'>
                                                    <label style={{ fontWeight: 'bold' }}>{formatCurrency((Math.round(toNumber(row.totalTradeAmount) * 100) / 100).toFixed(2))}</label>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className='widgetNoDataWithDate'>No transactions during the period</div>
                        )}
                    </>
                )}
            </CardBody>
        </Card>
    );
};

export default PortalTransactionSummary;
